import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/api';
import CopyInviteLinkButton from '../CopyInviteLinkButton';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Coach-scoped invitations for the active team: single invite (athlete,
 * coxswain, or coach) and bulk CSV invite. Extracted from Club Management's
 * club-wide invite section, but locked to one team and role subset.
 */
export default function TeamInvitesPanel({ team }) {
  const { token } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [message, setMessage] = useState('');

  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteRole, setNewInviteRole] = useState('athlete');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [messageIsError, setMessageIsError] = useState(false);

  const [showCsvInvite, setShowCsvInvite] = useState(false);
  const [csvInvitePreview, setCsvInvitePreview] = useState([]);
  const [csvInviteStatus, setCsvInviteStatus] = useState('');
  const [csvInviteResults, setCsvInviteResults] = useState(null);
  const [uploadingCsvInvites, setUploadingCsvInvites] = useState(false);
  const csvInviteInputRef = useRef(null);
  const [lastCreatedInvite, setLastCreatedInvite] = useState(null);

  const flash = (text, isError = false, ms = 3000) => {
    setMessage(text);
    setMessageIsError(isError);
    setTimeout(() => setMessage(''), ms);
  };

  useEffect(() => {
    if (team) fetchInvitations();
  }, [team]);

  const fetchInvitations = async () => {
    try {
      setLoadingInvites(true);
      const response = await fetch(`${API_URL}/api/invitations?team=${encodeURIComponent(team)}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const sendInvitation = async () => {
    if (!newInviteEmail.trim()) {
      flash('Email is required', true);
      return;
    }

    setSendingInvite(true);
    try {
      const response = await fetch(`${API_URL}/api/invitations`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          email: newInviteEmail.trim(),
          team,
          role: newInviteRole
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Existing club members are added to the team directly instead of
        // getting an email invite — surface whichever happened.
        flash(data.message || `Invitation sent to ${newInviteEmail}!`, false, 5000);
        if (data.existingMember) {
          setLastCreatedInvite(null);
        } else if (data.invitation?.inviteUrl) {
          setLastCreatedInvite({
            email: data.invitation.email || newInviteEmail.trim(),
            inviteUrl: data.invitation.inviteUrl,
            emailSent: !!data.emailSent
          });
        }
        setNewInviteEmail('');
        setNewInviteRole('athlete');
        fetchInvitations();
      } else {
        setLastCreatedInvite(null);
        flash(data.error || 'Failed to send invitation', true, 5000);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      flash('Error sending invitation', true);
    } finally {
      setSendingInvite(false);
    }
  };

  const cancelInvitation = async (inviteId) => {
    if (!confirm('Cancel this invitation?')) return;

    try {
      const response = await fetch(`${API_URL}/api/invitations/${inviteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        flash('Invitation cancelled');
        fetchInvitations();
      } else {
        const data = await response.json();
        flash(data.error || 'Failed to cancel invitation', true);
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      flash('Error cancelling invitation', true);
    }
  };

  const resendInvitation = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invitations/${inviteId}/resend`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        flash('Invitation resent!');
        fetchInvitations();
      } else {
        const data = await response.json();
        flash(data.error || 'Failed to resend invitation', true);
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      flash('Error resending invitation', true);
    }
  };

  const resetCsvInvite = () => {
    setCsvInvitePreview([]);
    setCsvInviteStatus('');
    setCsvInviteResults(null);
    if (csvInviteInputRef.current) {
      csvInviteInputRef.current.value = '';
    }
  };

  const handleCsvInviteFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvInviteStatus('');
    setCsvInviteResults(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase().replace(/[\s_]+/g, ''),
      complete: (results) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const seen = new Set();

        const rows = results.data.map((row, index) => {
          const email = (row.email || row.emailaddress || '').trim();
          const errors = [];

          if (!email) {
            errors.push('Missing email');
          } else if (!emailRegex.test(email)) {
            errors.push('Invalid email');
          } else if (seen.has(email.toLowerCase())) {
            errors.push('Duplicate in file');
          } else {
            seen.add(email.toLowerCase());
          }

          return {
            row: index + 2,
            email,
            errors,
            valid: errors.length === 0
          };
        });

        if (rows.length === 0) {
          setCsvInviteStatus('No rows found in CSV. Make sure it has an "Email" column header.');
          setCsvInvitePreview([]);
          return;
        }

        setCsvInvitePreview(rows);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        setCsvInviteStatus('Error parsing CSV file. Please check the format.');
      }
    });
  };

  const uploadCsvInvites = async () => {
    const validRows = csvInvitePreview.filter((r) => r.valid);
    if (validRows.length === 0) {
      setCsvInviteStatus('No valid rows to invite');
      return;
    }

    setUploadingCsvInvites(true);
    setCsvInviteStatus('');
    try {
      const response = await fetch(`${API_URL}/api/invitations/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          invitations: validRows.map((r) => ({ email: r.email, team }))
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCsvInviteResults(data.results);
        setCsvInvitePreview([]);
        if (csvInviteInputRef.current) {
          csvInviteInputRef.current.value = '';
        }
        fetchInvitations();
      } else {
        setCsvInviteStatus(data.error || 'Failed to send invitations');
      }
    } catch (error) {
      console.error('Error uploading CSV invites:', error);
      setCsvInviteStatus('Error sending invitations. Please try again.');
    } finally {
      setUploadingCsvInvites(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-1">Invite to {team}</h3>
      <p className="text-xs text-gray-500 mb-4">
        Invite athletes, coxswains, or coaches onto {team}. Existing RowCentral accounts at another
        club join with their current password. Copy the invite link if email is not delivered.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${messageIsError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {message}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-end">
        <div className="flex-1 min-w-48">
          <label className="block text-xs text-gray-600 mb-1">Email Address</label>
          <input
            type="email"
            placeholder="email@example.com"
            value={newInviteEmail}
            onChange={(e) => setNewInviteEmail(e.target.value)}
            className="border p-2 rounded w-full bg-white text-gray-900"
          />
        </div>
        <div className="w-28">
          <label className="block text-xs text-gray-600 mb-1">Role</label>
          <select
            value={newInviteRole}
            onChange={(e) => setNewInviteRole(e.target.value)}
            className="border p-2 rounded w-full bg-white text-gray-900"
          >
            <option value="athlete">Athlete</option>
            <option value="coxswain">Coxswain</option>
            <option value="coach">Coach</option>
          </select>
        </div>
        <button
          onClick={sendInvitation}
          disabled={sendingInvite || !newInviteEmail.trim()}
          className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
        >
          {sendingInvite ? 'Sending...' : 'Send Invite'}
        </button>
      </div>

      {lastCreatedInvite && (
        <div className="mb-4 border border-blue-200 bg-blue-50 rounded-lg p-3 text-sm text-blue-900">
          <p className="font-medium">
            Invite ready for {lastCreatedInvite.email}
            {lastCreatedInvite.emailSent ? ' (email marked sent).' : ' (email was not delivered).'}
          </p>
          <p className="text-xs break-all mt-1">{lastCreatedInvite.inviteUrl}</p>
          <CopyInviteLinkButton url={lastCreatedInvite.inviteUrl} className="mt-2 text-blue-700 hover:text-blue-900 text-xs font-semibold" />
        </div>
      )}

      <div className="mb-4">
        <button
          onClick={() => {
            setShowCsvInvite(!showCsvInvite);
            if (showCsvInvite) resetCsvInvite();
          }}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {showCsvInvite ? '− Hide bulk invite' : '+ Bulk invite via CSV'}
        </button>

        {showCsvInvite && (
          <div className="mt-3 border rounded-lg p-4 bg-gray-50">
            <p className="text-xs text-gray-600 mb-3">
              Upload a CSV with an <span className="font-semibold">Email</span> column (required).
              Everyone is invited as an athlete to {team}.
            </p>
            <input
              ref={csvInviteInputRef}
              type="file"
              accept=".csv"
              onChange={handleCsvInviteFileSelect}
              className="text-sm mb-3"
            />

            {csvInviteStatus && (
              <p className="text-sm text-red-600 mb-3">{csvInviteStatus}</p>
            )}

            {csvInvitePreview.length > 0 && (
              <div>
                <p className="text-sm text-gray-700 mb-2">
                  {csvInvitePreview.filter((r) => r.valid).length} of {csvInvitePreview.length} rows ready to invite
                </p>
                <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded bg-white mb-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 px-2 font-medium">Row</th>
                        <th className="py-2 px-2 font-medium">Email</th>
                        <th className="py-2 px-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvInvitePreview.map((row) => (
                        <tr key={row.row} className={`border-b ${row.valid ? '' : 'bg-red-50'}`}>
                          <td className="py-1.5 px-2 text-gray-500">{row.row}</td>
                          <td className="py-1.5 px-2">{row.email || '-'}</td>
                          <td className="py-1.5 px-2">
                            {row.valid ? (
                              <span className="text-green-600 text-xs">Ready</span>
                            ) : (
                              <span className="text-red-600 text-xs">{row.errors.join(', ')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={uploadCsvInvites}
                    disabled={uploadingCsvInvites || csvInvitePreview.filter((r) => r.valid).length === 0}
                    className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
                  >
                    {uploadingCsvInvites
                      ? 'Sending...'
                      : `Send ${csvInvitePreview.filter((r) => r.valid).length} Invite${csvInvitePreview.filter((r) => r.valid).length === 1 ? '' : 's'}`}
                  </button>
                  <button
                    onClick={resetCsvInvite}
                    disabled={uploadingCsvInvites}
                    className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {csvInviteResults && (
              <div className="mt-2">
                <p className="text-sm text-green-700 font-medium">
                  ✅ {csvInviteResults.sent} invitation{csvInviteResults.sent === 1 ? '' : 's'} sent
                  {csvInviteResults.addedExisting > 0 && `, ${csvInviteResults.addedExisting} existing member${csvInviteResults.addedExisting === 1 ? '' : 's'} added to the team`}
                  {csvInviteResults.skipped > 0 && `, ${csvInviteResults.skipped} skipped`}
                </p>
                {csvInviteResults.errors?.length > 0 && (
                  <ul className="mt-1 text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                    {csvInviteResults.errors.map((err, i) => (
                      <li key={i}>{err.email || '(no email)'}: {err.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          Pending Invitations ({invitations.filter((i) => !i.isExpired).length})
        </h4>
        {loadingInvites ? (
          <p className="text-gray-500 text-sm">Loading invitations...</p>
        ) : invitations.length === 0 ? (
          <p className="text-gray-500 text-sm">No pending invitations.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 font-medium">Email</th>
                  <th className="py-2 font-medium">Role</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invite) => (
                  <tr key={invite.id} className="border-b hover:bg-gray-50">
                    <td className="py-2">{invite.email}</td>
                    <td className="py-2 capitalize">{invite.role}</td>
                    <td className="py-2">
                      {invite.isExpired ? (
                        <span className="text-red-600 text-xs">Expired</span>
                      ) : (
                        <span className="text-green-600 text-xs">Pending</span>
                      )}
                    </td>
                    <td className="py-2 text-right space-x-2">
                      <CopyInviteLinkButton url={invite.inviteUrl} />
                      <button
                        onClick={() => resendInvitation(invite.id)}
                        className="text-blue-600 hover:text-blue-800 text-xs"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => cancelInvitation(invite.id)}
                        className="text-red-600 hover:text-red-800 text-xs"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
